import type { MarketingBannerRow } from "../types";

import { stripImpressionPixel } from "./helpers";

type Props = {
  banner: MarketingBannerRow;
  width: number;
  height: number;
  scale: number;
  onLoad?: () => void;
};

/**
 * Renders an HTML ad banner inside a sandboxed iframe. The banner is rendered
 * at its intrinsic dimensions, then scaled down via CSS transform so it fits
 * the parent box without altering the source layout.
 */
export const HTMLAdPreview = ({
  banner,
  width,
  height,
  scale,
  onLoad,
}: Props) => {
  // Style overrides applied inside the iframe:
  // - Remove default body margin so the banner aligns to (0,0).
  // - Hide overflow to prevent scrollbars inside the preview viewport.
  // - Disable pointer events so the iframe is non-interactive in the preview.
  // NOTE: The CSS transform (scale) is applied on the *iframe element* itself
  // in the outer document, not on the body inside. This keeps the iframe
  // viewport at the banner's intrinsic dimensions so its HTML renders at full
  // width, then the element-level transform scales it down visually.
  const styleOverrides = `
    <style>
      body {
        margin: 0;
        overflow: hidden;
      }

      * {
        pointer-events: none;
      }
    </style>
  `;

  // Strip the impression pixel before injecting — the pixel is a hidden
  // zero-size <img> whose only purpose is to fire a tracking request; rendering
  // it in the preview iframe adds a network call without any visible payload.
  // The copy-code path keeps the raw `creative_code` (with pixel) untouched.
  const srcDoc = `
    ${styleOverrides}
    ${stripImpressionPixel(banner.creative_code)}
  `;

  // Wrap the iframe in a container sized to the *scaled* dimensions. The
  // iframe itself is sized to the banner's intrinsic dimensions so its
  // internal viewport matches the banner's design width/height, then a CSS
  // transform on the element scales it down to fit. overflow:hidden clips
  // any visual bleed from the transform.
  return (
    <div style={{ width, height, overflow: "hidden" }}>
      <iframe
        id={`marketing-banner-${banner.id}`}
        title={banner.name}
        width={banner.width}
        height={banner.height}
        style={{
          transform: `scale(${scale})`,
          transformOrigin: "0 0",
          display: "block",
        }}
        src="data:text/html;charset=utf-8,"
        srcDoc={srcDoc}
        tabIndex={-1}
        aria-hidden="true"
        className="pointer-events-none border-0"
        onLoad={onLoad}
      />
    </div>
  );
};
